import { appBarMeta } from "@constants/appbar";
import FixedPodcastPlayer from "@features/podcasts/components/FixedPodcastPlayer";
import usePodcastPlayer from "@features/podcasts/hooks/usePodcastPlayer";
import { usePodcasts } from "@features/podcasts/hooks/usePodcasts";
import { useSavedPodcastsCache } from "@features/podcasts/hooks/useSavedPodcastsCache";
import { Citations } from "@features/session-config/components/Citations";
import { handleNavigateBack } from "@helpers/common";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import { ZoomImage } from "@memorang/ui/components/MarkdownRenderer/ZoomImage";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useToast } from "@memorang/ui/components/Toast";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import DocumentTitle from "components/DocumentTitle";
import { useLocalSearchParams, useNavigation } from "expo-router";
import type { Track } from "libs/audio-player";
import { useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Appbar, Chip, Text } from "react-native-paper";

const PodcastScreen = () => {
	const { podcastId } = useLocalSearchParams();
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { loadTracks } = usePodcastPlayer();
	const { isMobile } = useDevice();
	const { podcasts } = usePodcasts();
	const { isPodcastSaved, toggleSavedPodcast } = useSavedPodcastsCache();
	const { showToast } = useToast();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	const currentPodcastIndex = useMemo(
		() => podcasts.findIndex((a) => a.id === Number(podcastId)),
		[podcasts, podcastId],
	);

	const podcast = podcasts[currentPodcastIndex]!;

	useLayoutEffect(() => {
		const tracks: Track[] = podcasts.map((podcast) => ({
			url:
				podcast.metadata.podcast ||
				podcast.content.media?.find((media) => media.type === "AUDIO")?.url ||
				"",
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
			album: podcast.id.toString(),
		}));
		loadTracks({
			tracks,
			currentPodcastIndex,
			currentTrack: tracks[currentPodcastIndex],
			shouldReset: false,
		});
	}, [loadTracks, podcasts, currentPodcastIndex]);

	const handleBookmarkToggle = () => {
		toggleSavedPodcast(Number(podcastId));
		const message = isPodcastSaved(Number(podcastId))
			? "Removed from bookmarks"
			: "Added to bookmarks";
		showToast({
			message,
			action: {
				label: "Undo",
				action: () => {
					toggleSavedPodcast(Number(podcastId));
				},
			},
		});
	};

	const allImages = podcast.content.media?.filter(
		(media) => media.type === "IMAGE",
	);

	const finalImage =
		allImages && allImages.length > 0
			? allImages[0]?.url
			: podcast.metadata.thumbnail;

	const isSaved = isPodcastSaved(Number(podcastId));

	return (
		<>
			<CustomAppBar
				{...(isMobile
					? {
							leftIconType: "back" as const,
							onPressBack: () => handleNavigateBack("/podcasts"),
						}
					: { navigation })}
				title={podcast.content.title}
				breadcrumbs={[
					{
						label: t(appBarMeta.podcasts.title),
						action: () => handleNavigateBack("/podcasts"),
					},
					{ label: podcast.content.title },
				]}
				renderRight={() => (
					<Appbar.Action
						icon={isSaved ? "bookmark" : "bookmark-outline"}
						accessibilityLabel={
							isSaved ? "Remove from bookmarks" : "Add to bookmarks"
						}
						onPress={handleBookmarkToggle}
					/>
				)}
				hideCommandBar
			/>
			<DocumentTitle title={podcast.content.title} />
			<Box
				flex={1}
				paddingVertical={isMobile ? 0 : 32}
				paddingHorizontal={isMobile ? 0 : 32}
			>
				<ScrollContainer
					contentContainerStyle={{
						paddingTop: isMobile ? 16 : 32,
						paddingHorizontal: 16,
						paddingBottom: 150,
					}}
				>
					<Box gap={16}>
						<Box width="100%" paddingHorizontal={16} alignItems="center">
							<ZoomImage
								url={finalImage}
								allImages={[
									{
										url: finalImage,
										caption: podcast.content.title,
									},
								]}
								width={240}
								height={240}
								borderRadius={8}
							/>
						</Box>
						<Box gap={16}>
							<FixedPodcastPlayer />
						</Box>
						<Box gap={10}>
							{podcast.content.article && (
								<Box
									flexDirection="row"
									justifyContent="space-between"
									alignItems="center"
								>
									<Box>
										<Text
											variant="labelMedium"
											style={{ color: theme.colors.onSurfaceVariant }}
										>
											{podcast.metadata.author}
										</Text>
										<Text variant="headlineSmall" numberOfLines={2}>
											{podcast.content.title}
										</Text>
									</Box>
								</Box>
							)}
							{podcast.tags && podcast.tags.length > 0 && (
								<Box flexDirection="row" flexWrap="wrap" gap={8}>
									{podcast.tags.map((tag) => (
										<Chip
											compact
											key={`tag-${podcast.id}-${tag}`}
											textStyle={{
												fontSize: 12,
											}}
										>
											{tag.label}
										</Chip>
									))}
								</Box>
							)}
							{podcast.content.article && (
								<Box paddingTop={8}>
									<CustomMarkdownRenderer
										text={podcast.content.article}
										customStyles={{
											paragraph: {
												lineHeight: 24,
												marginBottom: 16,
											},
										}}
									/>
								</Box>
							)}
							{podcast.metadata.sourceUrl && (
								<Citations
									useWebColors
									citations={[
										{
											pageContent: podcast.content.article,
											title: podcast.content.title,
											url: podcast.metadata.sourceUrl,
											id: podcast.id,
										},
									]}
								/>
							)}
						</Box>
					</Box>
				</ScrollContainer>
			</Box>
		</>
	);
};

export default PodcastScreen;
